import React, { useEffect, useRef, useState } from "react";
import Table from "../Table/Table";
import Table_Buttons from "../Table_Button/Table_button";
import { API } from "../../Redux/actions/API";
import Navbar from "../Navbar/Navbar";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateAuth } from "../../Redux/actions/AdminAuthSlice";
import Spinner from "../Spinner/Spinner";
import { toast } from "react-toastify";

function UpdateWithdrawlInfo() {
  const { userId, jwt_token } = useSelector((state) => state.Auth);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [leftreferralApi, setLeftreferralApi] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [listPerpage, setListPerpage] = useState(10);

  // IMPORTANT:
  // Use ref instead of state so table does not re-render
  // on every key press.
  const inputAmountsRef = useRef({});

  // Track which row is updating
  const [updatingId, setUpdatingId] = useState(null);

  // --------------------------------------------------
  // Unauthorized
  // --------------------------------------------------
  const handleUnauthorized = () => {
    dispatch(
      updateAuth({
        AdminAuth: false,
        jwt_token: "",
        userId: null,
      })
    );

    sessionStorage.clear();
    localStorage.clear();

    navigate("/");
  };

  // --------------------------------------------------
  // GET Withdrawal Info
  // --------------------------------------------------
  const Withdrawl_API = async () => {
    setIsLoading(true);

    try {
      const response = await API.post(
        "/UpdateWithdrawlInfo",
        {
          loginid: userId,
          id: "",
          amount: "",
          type: "2",
        },
        {
          headers: {
            Authorization: jwt_token,
          },
        }
      );

      const data = response?.data?.data1?.[0] || [];

      console.log("Withdrawal Info:", data);

      const formattedData = data.map((item, index) => ({
        sr: index + 1,

        // IMPORTANT: unique ID
        id: item?.sID,

        amount: item?.LIMITS ?? 0,

        caption: item?.ClubDesc ?? "",
      }));

      console.log("Formatted Data:", formattedData);

      setLeftreferralApi(formattedData);

      // Do NOT initialize React state here.
      // Amounts are stored in inputAmountsRef.
    } catch (e) {
      console.log("Get Withdrawal API Error:", e);

      if (
        e?.response?.data?.showableMessage ===
        "Please login to access this resource"
      ) {
        handleUnauthorized();
      } else {
        toast.error(
          e?.response?.data?.showableMessage ||
            "Unable to load withdrawal information"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // --------------------------------------------------
  // UPDATE API
  // --------------------------------------------------
  const UpdateWithdrawlInfo_API = async (item) => {
    const rowId = item?.id;

    // Get value directly from ref
    const enteredAmount = inputAmountsRef.current[rowId];

    console.log("--------------------------------");
    console.log("Row ID:", rowId);
    console.log("Entered Amount:", enteredAmount);
    console.log("--------------------------------");

    // Validate ID
    if (rowId === undefined || rowId === null || rowId === "") {
      toast.error("Invalid row ID");
      return;
    }

    // Validate amount
    if (
      enteredAmount === undefined ||
      enteredAmount === null ||
      enteredAmount === "" ||
      !/^\d+$/.test(String(enteredAmount)) ||
      Number(enteredAmount) <= 0
    ) {
      toast.error("Please enter a valid integer amount");
      return;
    }

    // Prevent double click
    if (updatingId === rowId) {
      return;
    }

    const amount = Number(enteredAmount);

    setUpdatingId(rowId);

    try {
      const payload = {
        loginid: userId,
        id: rowId,
        amount: amount,
        type: "1",
      };

      console.log("Update API Payload:", payload);

      const response = await API.post(
        "/UpdateWithdrawlInfo",
        payload,
        {
          headers: {
            Authorization: jwt_token,
            "Content-Type": "application/json",
          },
        }
      );

      console.log(
        "Update Withdrawal Response:",
        response?.data
      );

      const message = response?.data?.data;

      if (
        message === "Success" ||
        response?.data?.status === "SUCCESS"
      ) {
        toast.success(
          "Withdrawal information updated successfully"
        );

        // Update table immediately
        setLeftreferralApi((prev) =>
          prev.map((row) =>
            String(row.id) === String(rowId)
              ? {
                  ...row,
                  amount: amount,
                }
              : row
          )
        );

        // Clear ONLY this row's input
        delete inputAmountsRef.current[rowId];

        // Clear actual input visually
        const input = document.querySelector(
          `input[data-withdrawal-id="${rowId}"]`
        );

        if (input) {
          input.value = "";
        }
      } else {
        toast.error(message || "Update failed");
      }
    } catch (e) {
      console.log(
        "Update Withdrawal API Error:",
        e?.response?.data || e
      );

      if (
        e?.response?.data?.showableMessage ===
        "Please login to access this resource"
      ) {
        handleUnauthorized();
      } else {
        toast.error(
          e?.response?.data?.showableMessage ||
            e?.response?.data?.message ||
            "Something went wrong"
        );
      }
    } finally {
      setUpdatingId(null);
    }
  };

  // --------------------------------------------------
  // LOAD DATA
  // --------------------------------------------------
  useEffect(() => {
    if (userId && jwt_token) {
      Withdrawl_API();
    }
  }, [userId, jwt_token]);

  // --------------------------------------------------
  // PAGINATION
  // --------------------------------------------------
  const indexOfLastPost = currentPage * listPerpage;

  const indexOfFirstPage =
    indexOfLastPost - listPerpage;

  const currentPostleft = leftreferralApi.slice(
    indexOfFirstPage,
    indexOfLastPost
  );

  // --------------------------------------------------
  // TABLE COLUMNS
  // --------------------------------------------------
  const columns = [
    {
      Header: "S.No.",
      accessor: "sr",
    },

    {
      Header: "Type",
      accessor: "caption",
    },

    {
      Header: "Amount",
      accessor: "amount",
    },

    // --------------------------------------------------
    // ENTER AMOUNT
    // --------------------------------------------------
    {
      Header: "Enter Amount",
      accessor: "enteramount",

      Cell: ({ row }) => {
        const item = row.original;

        return (
          <input
            type="text"
            className="form-control"
            placeholder="Enter Amount"
            inputMode="numeric"
            autoComplete="off"
            data-withdrawal-id={item.id}
            defaultValue=""
            disabled={updatingId === item.id}
            maxLength={7}
            onChange={(e) => {
              const value = e.target.value;
              // ONLY INTEGER
              if (!/^\d*$/.test(value)) {
                // Restore previous valid value
                e.target.value =
                  inputAmountsRef.current[item.id] || "";
                return;
              }

              // Store value WITHOUT causing React re-render
              inputAmountsRef.current[item.id] = value;
              console.log(
                "Input:",
                item.id,
                value
              );
            }}
          />
        );
      },
    },

    // --------------------------------------------------
    // UPDATE BUTTON
    // --------------------------------------------------
    {
      Header: "Action",
      accessor: "update",

      Cell: ({ row }) => {
        const item = row.original;

        const isUpdating =
          updatingId === item.id;

        return (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={isUpdating}
            onClick={() =>
              UpdateWithdrawlInfo_API(item)
            }
          >
            {isUpdating
              ? "Updating..."
              : "Update"}
          </button>
        );
      },
    },
  ];

  // --------------------------------------------------
  // UI
  // --------------------------------------------------
  return (
    <div>
      <Navbar />

      {userId === "1" ? (
        <div className="content-page">
          <div className="content">
            <div className="row">
              <div className="col-md-12">

                <div className="panel panel-primary">

                  <div className="panel-heading">
                    <h4 className="panel-title">
                      Update Withdrawal Info
                    </h4>
                  </div>

                  <div className="panel-body">
                    <div className="row">

                      {isLoading ? (
                        <Spinner />
                      ) : (
                        <div className="col-md-12">

                          <Table
                            data={currentPostleft}
                            columns={columns}
                          />

                          <Table_Buttons
                            indexOfFirstPage={
                              indexOfFirstPage
                            }
                            indexOfLastPost={
                              indexOfLastPost
                            }
                            setcurrentPage={
                              setCurrentPage
                            }
                            currentPage={
                              currentPage
                            }
                            totalData={
                              leftreferralApi.length
                            }
                            listPerpage={
                              listPerpage
                            }
                            handleRowsPerPageChange={
                              setListPerpage
                            }
                          />

                        </div>
                      )}

                    </div>
                  </div>

                </div>

              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="content-page">
          <div className="content">
            <div className="row">
              <div className="col-md-12">

                <div className="panel panel-primary">

                  <div className="panel-heading">
                    <h4 className="panel-title">
                      Update Withdrawal Info
                    </h4>
                  </div>

                  <div className="text-center mt-5">
                    <h3>
                      You are not authorized to access
                      this page.
                    </h3>
                  </div>

                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateWithdrawlInfo;